import { useState, useEffect} from "react";
import {CommonDialogBox} from "components";
import {Stack, DialogProps, InputLabel , Select, Tooltip} from '@mui/material';
import { Button } from '@mui/material';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { useDispatch, useSelector} from 'react-redux';
import { closeIndexedImageryDialog} from 'store/main/viewObjects';
import { RootState } from 'store';
import { styled } from "@mui/material/styles";
import { getCachedData } from "api/Cache";
import jwtService from 'services/jwtService';
import NotificationMsg from "../NotificationMsg";
import NotificationModel from 'components/notificationPanel/model/NotificationModel';
import { addNotification } from 'components/notificationPanel/store/dataSlice';
import {rawDataSources} from "constants/common";
import axios from 'axios';
import AuthService from "services/AuthService";
import EventsEmitter from "events/EventsEmitter";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { config } from "config";
import { trackEvent } from "components/Tracking";

function IndexedImagery(props:any){
    const org = config.org;
    const {project, catalogId, file_type, name, isFolder} = props;
    const dispatch = useDispatch();
    const data = getCachedData();

    const viewObject = useSelector((state: RootState) => state.main.viewObject);
    const { objSources, objName, objType, iistate:open } = viewObject;

    const [itemName, setItemName] = useState(`New ${name}`)
    const [itemNameError, setItemNameError] = useState(false)
    const [helperItemName, setHelperItemName] = useState('')
    const [source, setSource] = useState([])
    const [horizontalDatum, setHorizontalDatum] = useState('')
    const [verticalDatum, setVerticalDatum] = useState('')
    const [projections, setProjections] = useState('')
    const [horizontalUnits, setHorizontalUnits] = useState('')
    const [fileDate, setFileDate] = useState('')

    const [coordOriginX, setCoordOriginX] = useState('')
    const [coordOriginY, setCoordOriginY] = useState('')
    const [coordOriginZ, setCoordOriginZ] = useState('')
    const [voxelSize, setVoxelSize] = useState('')
       
    const closeComposeDialog: DialogProps["onClose"] = (event, reason) => {
        if (reason && reason === "backdropClick")
            return;
        if(reason === "escapeKeyDown")
            cancel()
    }

	const cancel = () => {
        trackEvent("Process", "Indexed Imagery", "Close");
        dispatch(closeIndexedImageryDialog({}))
        setSource([])
	}
 
    const handleName = (e:any) => {
        setItemName(e.target.value)
        console.log(e.target.value)
        if(e.target.value === ''){
            setItemNameError(true);
            setHelperItemName("Please select a name");
        }else{
            setItemNameError(false);
            setHelperItemName("");
        }
    }

   
    const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
        "&:hover": {
            backgroundColor: "#1275aa", // light blue hover
            color: "#fff",
        },
        "&.Mui-selected": {
            backgroundColor: "#fff",
            position: "relative",
        },
        "&.Mui-selected:hover": {
            backgroundColor: "#1275aa",
            color: "#fff"
        },
        "&.Mui-selected::after": {
            content: '""',
            position: "absolute",
            right: 12,
            top: "50%",
            transform: "translateY(-50%)",
            width: 16,
            height: 16,
            backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='black' viewBox='0 0 24 24'><path d='M20.285 6.709l-11.285 11.293-5.285-5.293 1.41-1.41 3.875 3.879 9.875-9.879z'/></svg>")`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "contain",
        },
    }));
    
    const projectFiles = objSources.filter(
        (item: any) => item.file_folder === "0" && item.type === objType
    );

    const folderItems = objSources
    .filter((item: any) => item.type === "FOLDER" && item.project === project)
    .flatMap((folder: any) => {
        const files = objSources.filter(
            (item: any) => item.file_folder === folder.id && item.type === objType
        );

        if (files.length === 0) return [];

        return [
            <MenuItem disabled key={`${folder.id}-header`}>
                {folder.name.toUpperCase()}
            </MenuItem>,
            ...files.map((file: any) => (
                <CustomMenuItem value={file.id} key={file.id}>
                    {file.name}
                </CustomMenuItem>
            )),
            <div style={{ margin:'5px 0', borderTop : '1px solid #ededed' }}></div>
        ];
    });

    // Combine folderItems and projectFiles
    const allMenuItems = [
        ...folderItems,
        ...(projectFiles.length > 0
            ? [
                <MenuItem disabled key="project-header">PROJECT CONTENTS</MenuItem>,
                ...projectFiles.map((file: any) => (
                    <CustomMenuItem value={file.id} key={file.id}>
                        {file.name}
                    </CustomMenuItem>
                )),
            ]
            : []),
    ];

    
    const DialogAction = () => {
        return (
            <>
                <Button onClick={cancel}>Cancel</Button>
                <Button 
                    variant="contained"
                    color="primary"
                    type="submit" onClick={() => {
                        trackEvent("Process", "Indexed Imagery", "Create");
                        create();
                    }}
                    sx={{
                        '&.Mui-disabled': {
                            backgroundColor: (theme) => theme.palette.background.default,
                            color: (theme) => theme.palette.text.disabled,
                            opacity: '.75'
                        },
                    }}
                >
                    Create
                </Button>
            </>
        )
    }


    const create = async () => {
        
        const payload = {
            name: itemName,
            source : source.join(" "),
            capture_date: Date.now().toString(),
            type: "FILE",
            state: "PARTIAL",
            file_type: file_type,
            voxel_size: voxelSize,
            file_size: "0",
            file_date: fileDate,
            source_type : "ORTHO",
            file_folder: isFolder ? catalogId : "0",
            coord_origin_x: coordOriginX,
            coord_origin_y: coordOriginY,
            coord_origin_z: coordOriginZ,
            coord_hdatum: horizontalDatum,
            coord_vdatum: verticalDatum,
            coord_projection: projections,
            coord_unit: horizontalUnits,
        }
        
        const response = await jwtService.fetchRequest({
			method: 'POST',
			url: `/entity.ashx?project=${project}`,
			data: payload,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }
		});

		const data = response.data;

        if(data.result === 'success') {
            const id = data.id;
            const formData = new FormData();
            formData.append("process", `IMPORT_${file_type}`);

            const response = await axios.post(`/events.ashx?project=${project}&id=${id}&org=${org}`, formData, {
            headers: { 
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${AuthService.getToken()}`,
                },
            });
            if(response.data.result === 'success'){
                dispatch(addNotification(NotificationModel({ message: <NotificationMsg msg={itemName}/>, options: { variant: 'success' } })));
                EventsEmitter.emit("reloadData", "");
            }else{
                dispatch(addNotification(NotificationModel({ message: 'An error occurs during the process upload', options: { variant: 'error' } })));
            }
    
        }else{
            dispatch(addNotification(NotificationModel({ message: 'An error occurs during the process upload', options: { variant: 'error' } })));
        }
       
        setTimeout(() => {
            dispatch(closeIndexedImageryDialog({}))
        }, 100);
    }

    useEffect(() => {     
        const projects: any = data["projects"][project];
        setHorizontalDatum(projects['coord_hdatum'])
        setVerticalDatum(projects['coord_vdatum'])
        setProjections(projects['coord_projection'])
        setHorizontalUnits(projects['coord_unit'])
        setFileDate(projects['file_date'])
        setCoordOriginX(projects['coord_origin_x'])
        setCoordOriginY(projects['coord_origin_y'])
        setCoordOriginZ(projects['coord_origin_z'])
        setVoxelSize(projects['voxel_size'])
    }, [data, project])

    
    return (
		<>
            <CommonDialogBox
                classes={{
                    paper: 'm-24 rounded-8'
                }}
                open={open}
                handleClose={closeComposeDialog}
                fullWidth
                maxWidth="sm"
                subtitle1={`New ${objName}`}
                titleh5=''
                dialogAction={objSources.filter((item:any) => item.type === objType && item.project === project).length > 0 ? <DialogAction /> : ''}
                displayScrollBar={true}
                maxHeight={600}
            >       
            {
                objSources.filter((item:any) => item.type === objType && item.project === project).length > 0 ?
              
                <Stack 
                    gap={1}
                >
                    <InputLabel id="itemName-label">Name</InputLabel>
                    <Tooltip title="A descriptive, readable name for the new entity" placement="top" arrow>
                    <TextField
                        id="itemName"
                        name="itemName"
                        error={itemNameError}
                        helperText={helperItemName}
                        value={itemName}
                        type="txt"
                        fullWidth
                        color='primary'
                        variant="outlined"
                        inputProps={{
                            style: {
                              height: '5px',
                            },
                        }}
                        onChange={handleName}
                    /> 
                    </Tooltip>
                    <InputLabel id="voxsurf-select-pc-label">Source</InputLabel>

                    <Tooltip title={
                        <>
                        Select which files to upload as the Ortho-Imagery set. Each image in the set is expected to have a matching world registration file. 
                        Click on the help <HelpOutlineIcon fontSize="inherit" /> above for more details.
                        </>
                    } placement="top" arrow>

                        <Select
                            required
                            margin="dense"
                            id="voxsurf-select-pc"
                            name="voxsurf-select-pc"
                            label="Source"
                            value={source}
                            multiple
                            type="txt"
                            fullWidth
                            color='primary'
                            variant="outlined"
                            onChange={(e:any) => setSource(e.target.value)}
                        >
                            {
                                //objSources.filter((item:any) => item.type === objType && item.project === project).map((elem:any, index:any) => <CustomMenuItem value={elem.id} key={index}>{elem.name}</CustomMenuItem>)
                                allMenuItems
                            }
                        </Select> 
                    </Tooltip>
                </Stack>
                :
                <div style={{display:'flex', justifyContent:'center',alignItems: 'center', height: '30vh'}}>
                    {
                    `No "${rawDataSources[objType]}" data source found`
                    }
                </div>
                }
            </CommonDialogBox>
		</>
  	)
}

export default IndexedImagery